import {
  runtimeScopedSidebarKey,
  type SavedEnvironmentRuntimeIndex,
} from "./sidebarThreadRuntimeIdentity";

/**
 * Merge the live environment store with CMMD's durable runtime replica.
 * A live row is authoritative once its runtime is attached, while replica-only
 * rows keep offline runtimes readable. Identity must remain runtime-scoped
 * because independent runtimes can legitimately reuse a provider thread ID.
 * See `sidebarThreadRuntimeIdentity` for why the environment key alone is not
 * enough.
 */

export function mergeLiveAndReplicatedSidebarThreads<
  TThread extends { readonly environmentId: string; readonly id: string },
>(
  liveThreads: readonly TThread[],
  replicatedThreads: readonly TThread[],
  environments: SavedEnvironmentRuntimeIndex = {},
): readonly TThread[] {
  const merged: TThread[] = [];
  const seenKeys = new Set<string>();

  const appendUnseen = (threads: readonly TThread[]) => {
    for (const thread of threads) {
      const key = runtimeScopedSidebarKey(thread.environmentId, thread.id, environments);
      if (seenKeys.has(key)) continue;
      seenKeys.add(key);
      merged.push(thread);
    }
  };

  appendUnseen(liveThreads);
  appendUnseen(replicatedThreads);
  return merged;
}
